import { useMemo, useContext } from "react";
import { getSpacingClass } from "../utils/spacingUtils";
import { default as PreviewContext } from "../../editor/context/PreviewContext";
// Note: We import the context directly and use useContext here to avoid throwing
// when used outside of a PreviewProvider. Do not call usePreview() inside helpers.

const MAXWIDTH_CENTERING_DEBUG = "MAXWIDTH_CENTERING_DEBUG";
const __isDev = process.env.NODE_ENV === "development";

const DEFAULT_MARGIN = "m-0";
const DEFAULT_PADDING = "p-0";

/**
 * Custom hook to manage margin and padding classes
 * @param {Object} component - Component object containing margin and padding data
 * @param {boolean} ignoreSpacing - Flag to ignore spacing logic
 * @returns {Object} - Object containing margin and padding classes and a combined class string
 */
const useSpacing = (
  component = null,
  ignoreSpacing = false,
  autoMargin = null,
  autoPadding = null,
  ignoreMargin = false,
  ignorePadding = false,
  widthOverride = null,
) => {
  const preview = useContext(PreviewContext);
  const { margin, padding, combinedClasses } = useMemo(() => {
    if (ignoreSpacing) {
      return {
        margin: DEFAULT_MARGIN,
        padding: DEFAULT_PADDING,
        combinedClasses: `${DEFAULT_PADDING} ${DEFAULT_MARGIN}`.trim(),
      };
    }

    if (!component) {
      return {
        margin: DEFAULT_MARGIN,
        padding: DEFAULT_PADDING,
        combinedClasses: `${DEFAULT_PADDING} ${DEFAULT_MARGIN}`.trim(),
      };
    }

    const resolveBP = (byLayout) => {
      if (!byLayout || typeof byLayout !== "object") return null;
      // Prefer PreviewContext contentWidth when available; fallback chain to viewport then window width
      let w = typeof window !== "undefined" ? window.innerWidth : 1920;
      let widthSource = "window";
      if (typeof widthOverride === "number" && Number.isFinite(widthOverride) && widthOverride > 0) {
        w = widthOverride;
        widthSource = "widthOverride";
      } else if (preview && typeof preview.contentWidth === "number") {
        w = preview.contentWidth;
        widthSource = "preview.contentWidth";
      } else if (preview && preview.mode !== "responsive" && preview.viewport?.width) {
        w = preview.viewport.width;
        widthSource = "preview.viewport.width";
      }
      
      // Determine breakpoint key
      let selectedKey = "extra_large";
      if (w < 640) selectedKey = "extra_small";
      else if (w < 768) selectedKey = "small";
      else if (w < 1024) selectedKey = "medium";
      else if (w < 1280) selectedKey = "large";
      
      // Debug logging (dev-only). Keep it narrow to avoid console spam.
      if (__isDev && (component?.type === "website_page" || widthSource === "widthOverride")) {
        try {
          console.log(MAXWIDTH_CENTERING_DEBUG, "useSpacing breakpoint", {
            componentType: component?.type,
            componentId: component?.id,
            width: w,
            widthSource,
            selectedKey,
            previewMode: preview?.mode,
          });
        } catch (_) {}
      }
      
      // Return value for selected key with fallbacks
      if (w < 640) return byLayout.extra_small || byLayout.small || byLayout.medium || byLayout.large || byLayout.extra_large;
      if (w < 768) return byLayout.small || byLayout.extra_small || byLayout.medium || byLayout.large || byLayout.extra_large;
      if (w < 1024) return byLayout.medium || byLayout.large || byLayout.small || byLayout.extra_large || byLayout.extra_small;
      if (w < 1280) return byLayout.large || byLayout.medium || byLayout.extra_large || byLayout.small || byLayout.extra_small;
      return byLayout.extra_large || byLayout.large || byLayout.medium || byLayout.small || byLayout.extra_small;
    };

    const currentMargin = resolveBP(component.margin);
    const currentPadding = resolveBP(component.padding);

    const computedMargin = getSpacingClass(currentMargin, "margin", ignoreMargin, autoMargin);
    const computedPadding = getSpacingClass(currentPadding, "padding", ignorePadding, autoPadding);

    return {
      margin: computedMargin,
      padding: computedPadding,
      combinedClasses: `${computedPadding} ${computedMargin}`.trim(),
    };
  }, [
    component,
    ignoreSpacing,
    ignoreMargin,
    ignorePadding,
    autoMargin,
    autoPadding,
    widthOverride,
    preview?.mode,
    preview?.viewport?.width,
    preview?.contentWidth,
  ]);

  return {
    margin,
    padding,
    combinedClasses,
  };
};

export default useSpacing;
